import React, { useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  toSqft, 
  fromSqft,
  getAllConversions,
  getDefaultUnit, 
  validateAreaInput,
  getConversionPreview,
  AREA_UNIT_OPTIONS,
  UNIT_DISPLAY_NAMES
} from '@/utils/areaConverter';
import { Ruler, RefreshCw } from 'lucide-react';

/**
 * AreaInput - Reusable property area input component with unit conversion
 * 
 * @param {Object} props
 * @param {number} props.value - Area value in the selected unit
 * @param {string} props.unit - Selected unit (sqft, sqyd, acres, guntas, cents)
 * @param {number} props.sqftValue - Area in square feet (for controlled mode)
 * @param {function} props.onChange - Callback with { value, unit, sqftValue }
 * @param {string} props.propertyType - Property type for default unit selection
 * @param {boolean} props.showPreview - Show conversion preview (default: true)
 * @param {boolean} props.showLabel - Show label (default: true)
 * @param {string} props.label - Custom label text
 * @param {string} props.error - External error message
 * @param {boolean} props.required - Is field required
 * @param {boolean} props.disabled - Disable input
 * @param {string} props.className - Additional container classes
 */
const AreaInput = ({
  value: externalValue,
  unit: externalUnit,
  sqftValue: externalSqftValue,
  onChange,
  propertyType,
  showPreview = true,
  showLabel = true,
  label = 'Area',
  error: externalError,
  required = false,
  disabled = false,
  className = ''
}) => {
  // Internal state
  const [inputValue, setInputValue] = useState(externalValue || '');
  const [selectedUnit, setSelectedUnit] = useState(externalUnit || getDefaultUnit(propertyType));
  const [localError, setLocalError] = useState(null);
  const [conversions, setConversions] = useState({});
  
  // Initialize from external sqftValue if provided
  useEffect(() => {
    if (externalSqftValue && externalSqftValue > 0 && !externalValue) {
      const unit = externalUnit || getDefaultUnit(propertyType);
      const convertedValue = fromSqft(externalSqftValue, unit);
      setInputValue(convertedValue);
      setSelectedUnit(unit);
      setConversions(getAllConversions(externalSqftValue));
    }
  }, [externalSqftValue, externalUnit, externalValue, propertyType]);
  
  // Sync with external value changes
  useEffect(() => {
    if (externalValue !== undefined && externalValue !== inputValue) {
      setInputValue(externalValue);
    }
  }, [externalValue]);
  
  // Sync with external unit changes
  useEffect(() => {
    if (externalUnit && externalUnit !== selectedUnit) {
      setSelectedUnit(externalUnit);
    }
  }, [externalUnit]);
  
  // Update default unit when property type changes
  useEffect(() => {
    if (propertyType && !externalUnit) {
      const defaultUnit = getDefaultUnit(propertyType);
      if (defaultUnit !== selectedUnit && !inputValue) {
        setSelectedUnit(defaultUnit);
      }
    }
  }, [propertyType, externalUnit, selectedUnit, inputValue]);
  
  // Calculate conversions when value or unit changes
  const updateConversions = useCallback((val, unit) => {
    const numValue = parseFloat(val);
    if (!isNaN(numValue) && numValue > 0) {
      const sqft = toSqft(numValue, unit);
      setConversions(getAllConversions(sqft));
      return sqft;
    } else {
      setConversions({});
      return 0;
    }
  }, []);
  
  // Handle value change
  const handleValueChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Validate
    const { isValid, error, sqftValue } = validateAreaInput(newValue, selectedUnit);
    setLocalError(isValid ? null : error);
    
    // Update conversions
    if (isValid) {
      setConversions(getAllConversions(sqftValue));
    }
    
    // Notify parent
    if (onChange) {
      onChange({
        value: newValue ? parseFloat(newValue) : null,
        unit: selectedUnit,
        sqftValue: isValid ? sqftValue : null,
        isValid
      });
    }
  };
  
  // Handle unit change
  const handleUnitChange = (newUnit) => {
    const numValue = parseFloat(inputValue);
    
    if (!isNaN(numValue) && numValue > 0) {
      // Convert existing value to new unit
      const currentSqft = toSqft(numValue, selectedUnit);
      const convertedValue = fromSqft(currentSqft, newUnit);
      
      setSelectedUnit(newUnit);
      setInputValue(convertedValue);
      
      // Notify parent with converted value
      if (onChange) {
        onChange({
          value: convertedValue,
          unit: newUnit,
          sqftValue: currentSqft,
          isValid: true
        });
      }
    } else {
      setSelectedUnit(newUnit);
      
      if (onChange) {
        onChange({
          value: inputValue ? parseFloat(inputValue) : null,
          unit: newUnit,
          sqftValue: null,
          isValid: false
        });
      }
    }
  };
  
  const displayError = externalError || localError;
  const previewItems = showPreview ? getConversionPreview(conversions.sqft || 0, selectedUnit, 3) : [];
  
  return (
    <div className={`space-y-2 ${className}`} data-testid="area-input-container">
      {showLabel && (
        <Label className="flex items-center gap-2">
          <Ruler className="w-4 h-4 text-primary" />
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
      )}
      
      <div className="flex gap-2">
        {/* Value Input */}
        <div className="flex-1">
          <Input
            type="number"
            step="0.01"
            min="0"
            placeholder="Enter area"
            value={inputValue}
            onChange={handleValueChange}
            disabled={disabled}
            className={displayError ? 'border-red-500' : ''}
            data-testid="area-value-input"
          />
        </div>
        
        {/* Unit Selector */}
        <div className="w-32">
          <Select 
            value={selectedUnit} 
            onValueChange={handleUnitChange}
            disabled={disabled}
          >
            <SelectTrigger data-testid="area-unit-select">
              <SelectValue placeholder="Unit" />
            </SelectTrigger>
            <SelectContent>
              {AREA_UNIT_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Error Message */}
      {displayError && (
        <p className="text-red-500 text-xs" data-testid="area-error">
          {displayError}
        </p>
      )}
      
      {/* Conversion Preview */}
      {showPreview && previewItems.length > 0 && !displayError && (
        <div className="bg-gray-50 rounded-lg p-3 mt-2" data-testid="area-preview">
          <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-2">
            <RefreshCw className="w-3 h-3" />
            <span>Equivalent Area</span>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {previewItems.map(item => (
              <span 
                key={item.unit} 
                className="text-sm text-gray-700"
                data-testid={`area-preview-${item.unit}`}
              >
                ≈ {item.formatted}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AreaInput;
